import type { Client, ClientStatus } from '../types'

export type PriorityLevel = 'urgent' | 'high' | 'normal' | 'low'

export interface PriorityResult {
  score: number
  level: PriorityLevel
  label: string
  reasons: string[]
}

const DAY = 86_400_000

function dayStart(value: Date): number {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
}

function add(
  reasons: string[],
  text: string,
  points: number,
): number {
  reasons.push(`${text} (+${points})`)
  return points
}

function statusPoints(status: ClientStatus, reasons: string[]): number {
  switch (status) {
    case 'new':
      return add(reasons, 'контакта ещё не было', 28)
    case 'callback':
      return add(reasons, 'нужно вернуться к клиенту', 34)
    case 'interested':
      return add(reasons, 'клиент проявил интерес', 30)
    case 'no_answer':
      return add(reasons, 'до клиента не дозвонились', 18)
    case 'contacted':
      return add(reasons, 'контакт уже состоялся', 8)
    case 'not_interested':
      reasons.push('клиент отметил предложение как неактуальное (−20)')
      return -20
  }
}

export function calculatePriority(
  client: Client,
  now = new Date(),
): PriorityResult {
  const reasons: string[] = []
  let score = statusPoints(client.status, reasons)

  const potential = client.potential ?? 0
  if (potential >= 1_000_000) {
    score += add(reasons, 'высокий потенциал', 25)
  } else if (potential >= 500_000) {
    score += add(reasons, 'заметный потенциал', 18)
  } else if (potential >= 100_000) {
    score += add(reasons, 'есть коммерческий потенциал', 10)
  }

  const revenue = client.osint?.revenue ?? 0
  if (revenue >= 10_000_000_000) {
    score += add(reasons, 'крупнейшая выручка по открытому отчёту', 16)
  } else if (revenue >= 1_000_000_000) {
    score += add(reasons, 'выручка свыше 1 млрд ₽', 12)
  } else if (revenue >= 100_000_000) {
    score += add(reasons, 'выручка свыше 100 млн ₽', 8)
  } else if (revenue >= 50_000_000) {
    score += add(reasons, 'выручка свыше 50 млн ₽', 5)
  }

  if (client.osint?.governmentContracts === 'yes') {
    score += add(reasons, 'подтверждена закупочная активность', 7)
  }

  if (client.osint?.rank && client.osint.rank <= 20) {
    score += add(reasons, 'входит в топ-20 OSINT-рейтинга', 6)
  }

  if (client.nextContactAt) {
    const due = dayStart(new Date(client.nextContactAt))
    const today = dayStart(now)
    const delta = Math.round((due - today) / DAY)

    if (delta < 0) {
      score += add(reasons, 'просрочен следующий контакт', 40)
    } else if (delta === 0) {
      score += add(reasons, 'контакт запланирован на сегодня', 28)
    } else if (delta <= 3) {
      score += add(reasons, 'контакт нужен в ближайшие три дня', 12)
    }
  }

  if (client.lastContactAt) {
    const days = Math.floor((now.getTime() - new Date(client.lastContactAt).getTime()) / DAY)
    if (days >= 14) {
      score += add(reasons, 'давно не было контакта', 18)
    } else if (days >= 7) {
      score += add(reasons, 'прошла неделя после контакта', 8)
    }
  } else if (client.status === 'new') {
    score += add(reasons, 'нет истории взаимодействий', 10)
  }

  if (client.status === 'no_answer' && client.attempts >= 3) {
    score -= 8
    reasons.push('уже было три попытки дозвона (−8)')
  }

  score = Math.max(0, Math.min(100, score))
  const level: PriorityLevel =
    score >= 70 ? 'urgent' : score >= 45 ? 'high' : score >= 25 ? 'normal' : 'low'
  const label =
    level === 'urgent'
      ? 'Срочно'
      : level === 'high'
        ? 'Высокий'
        : level === 'normal'
          ? 'Обычный'
          : 'Низкий'

  return { score, level, label, reasons }
}

export function sortByPriority(clients: Client[], now = new Date()): Client[] {
  return [...clients].sort((left, right) => {
    const scoreDelta = calculatePriority(right, now).score - calculatePriority(left, now).score
    if (scoreDelta !== 0) return scoreDelta

    if (left.nextContactAt && right.nextContactAt) {
      return new Date(left.nextContactAt).getTime() - new Date(right.nextContactAt).getTime()
    }
    if (left.nextContactAt) return -1
    if (right.nextContactAt) return 1
    return left.company.localeCompare(right.company, 'ru')
  })
}

export function hasSpoken(status: ClientStatus): boolean {
  return status !== 'new'
}

export function isDue(client: Client, now = new Date()): boolean {
  if (!client.nextContactAt) return false
  return dayStart(new Date(client.nextContactAt)) <= dayStart(now)
}
